// src/services/maps/overlays.js
// Tile overlay management — NOAA radar, FEMA flood zones, NWS warnings.
// All overlay instances are stored here; components call show/hide/toggle.
// The map instance is passed in from MapManager — never created here.

import L from 'leaflet';
import { OVERLAYS, ZOOM_THRESHOLDS } from './config.js';

// ── Overlay registry ──────────────────────────────────────────────
// Holds live L.TileLayer instances keyed by overlay id.
const _layers  = new Map();   // id → L.TileLayer | L.LayerGroup
const _visible = new Set();   // ids currently added to the map

// ── Add a single tile overlay ─────────────────────────────────────
function _createTileOverlay(cfg) {
  return L.tileLayer(cfg.url, {
    attribution: cfg.attribution ?? '',
    opacity:     cfg.opacity     ?? 0.6,
    maxZoom:     cfg.maxZoom     ?? 18,
    zIndex:      cfg.zIndex      ?? 200,
    crossOrigin: true,
  });
}

// ── Public API ────────────────────────────────────────────────────
export const OverlayManager = {

  // Show an overlay by id. Creates the layer the first time.
  show(map, id) {
    if (!map || _visible.has(id)) return;
    const cfg = OVERLAYS[id];
    if (!cfg || cfg.type !== 'tile') return;  // geojson overlays handled by layer-specific renderers

    if (!_layers.has(id)) {
      _layers.set(id, _createTileOverlay(cfg));
    }
    _layers.get(id).addTo(map);
    _visible.add(id);
  },

  // Hide an overlay by id.
  hide(map, id) {
    if (!_visible.has(id)) return;
    const layer = _layers.get(id);
    if (layer && map) map.removeLayer(layer);
    _visible.delete(id);
  },

  // Toggle an overlay.
  toggle(map, id) {
    _visible.has(id) ? OverlayManager.hide(map, id) : OverlayManager.show(map, id);
  },

  // Set exactly these overlays as visible (for mode switching).
  setActive(map, ids = []) {
    // Hide currently visible overlays that aren't in new set
    for (const id of [..._visible]) {
      if (!ids.includes(id)) OverlayManager.hide(map, id);
    }
    // Show new overlays
    for (const id of ids) OverlayManager.show(map, id);
  },

  // Set opacity on a specific overlay (0-1).
  setOpacity(id, opacity) {
    _layers.get(id)?.setOpacity(opacity);
  },

  // Is the overlay currently visible?
  isVisible(id) { return _visible.has(id); },

  // Clean up all overlays from the map.
  destroyAll(map) {
    for (const [id, layer] of _layers) {
      if (map) map.removeLayer(layer);
    }
    _layers.clear();
    _visible.clear();
  },
};

// ── NOAA Radar refresh ────────────────────────────────────────────
// Radar tiles are time-sensitive. Redraw the layer every refreshMs.
let _radarRefreshTimer = null;

export function startRadarRefresh(map, intervalMs = 5 * 60_000) {
  stopRadarRefresh();
  _radarRefreshTimer = setInterval(() => {
    const layer = _layers.get('noaa_radar');
    if (layer && _visible.has('noaa_radar')) {
      // Force a tile reload by setting the URL again with a cache-bust param
      const base = OVERLAYS.noaa_radar.url;
      layer.setUrl(base + `?t=${Date.now()}`);
    }
  }, intervalMs);
}

export function stopRadarRefresh() {
  clearInterval(_radarRefreshTimer);
  _radarRefreshTimer = null;
}

// ── Flood zone polygon renderer ───────────────────────────────────
// When individual FEMA zone polygons are available (from our seed data
// or a future FEMA API call), this renders them as colored polygons
// instead of the tile overlay — gives us full control over styling.

let _floodPolygonGroup = null;

export function renderFloodZonePolygons(map, zones = []) {
  if (_floodPolygonGroup) {
    map.removeLayer(_floodPolygonGroup);
    _floodPolygonGroup = null;
  }
  if (!zones.length) return;

  const features = zones.map((zone) => {
    if (!zone.polygon?.length) return null;
    return L.polygon(zone.polygon, {
      fillColor:   zone.fillColor   ?? '#0369a1',
      fillOpacity: zone.fillOpacity ?? 0.2,
      color:       zone.fillColor   ?? '#0369a1',
      weight:      1,
      opacity:     0.6,
    }).bindTooltip(`${zone.zoneType} — ${zone.label}`, { sticky: true });
  }).filter(Boolean);

  _floodPolygonGroup = L.layerGroup(features).addTo(map);
  return _floodPolygonGroup;
}

export function clearFloodZonePolygons(map) {
  if (_floodPolygonGroup) {
    map.removeLayer(_floodPolygonGroup);
    _floodPolygonGroup = null;
  }
}

// ── Evacuation route renderer ─────────────────────────────────────
// Renders evac routes as styled polylines.
// Routes with polyline data get drawn; others are skipped.

import { EVAC_ROUTE_STYLE } from './config.js';

let _evacRouteGroup = null;

export function renderEvacRoutes(map, routes = [], onClick) {
  if (_evacRouteGroup) {
    map.removeLayer(_evacRouteGroup);
    _evacRouteGroup = null;
  }
  if (!routes.length) return;

  const lines = routes.map((route) => {
    const style = EVAC_ROUTE_STYLE[route.status] ?? EVAC_ROUTE_STYLE.unknown;

    // If we have a polyline, use it; otherwise skip
    if (!route.polyline?.length) return null;

    const line = L.polyline(route.polyline, {
      ...style,
      interactive: true,
    });

    const statusLabel = route.contraflowActive
      ? 'CONTRAFLOW ACTIVE'
      : route.status.toUpperCase();

    line.bindPopup(`
      <div style="font-family:system-ui">
        <div style="font-weight:900;font-size:14px;margin-bottom:4px">${route.name}</div>
        <div style="font-size:11px;color:${style.color};font-weight:800;margin-bottom:4px">
          ${statusLabel}
        </div>
        ${route.direction ? `<div style="font-size:11px;color:#78716c">${route.direction}</div>` : ''}
        ${route.note      ? `<div style="font-size:11px;color:#78716c;margin-top:4px">${route.note}</div>` : ''}
      </div>
    `);

    if (onClick) line.on('click', () => onClick(route));
    return line;
  }).filter(Boolean);

  _evacRouteGroup = L.layerGroup(lines).addTo(map);
  return _evacRouteGroup;
}

export function clearEvacRoutes(map) {
  if (_evacRouteGroup) {
    map.removeLayer(_evacRouteGroup);
    _evacRouteGroup = null;
  }
}

// ── User location accuracy ring ───────────────────────────────────
let _accuracyCircle = null;
let _userMarker     = null;

export function renderUserLocation(map, lat, lon, accuracyM, icons) {
  // Remove old
  _accuracyCircle && map.removeLayer(_accuracyCircle);
  _userMarker     && map.removeLayer(_userMarker);

  // Accuracy ring
  _accuracyCircle = L.circle([lat, lon], {
    radius:      accuracyM,
    color:       '#0369a1',
    fillColor:   '#0369a1',
    fillOpacity: 0.08,
    weight:      1,
  }).addTo(map);

  // User dot
  _userMarker = L.marker([lat, lon], { icon: icons.user(), zIndexOffset: 9000 })
    .bindTooltip('You are here', { permanent: false })
    .addTo(map);
}

export function clearUserLocation(map) {
  _accuracyCircle && map.removeLayer(_accuracyCircle);
  _userMarker     && map.removeLayer(_userMarker);
  _accuracyCircle = null;
  _userMarker     = null;
}
